import { get, isFinite } from 'lodash';

import {
  GIFT_CART_MODEL_PROPERTY_IS_DGC_WITH_RECIPIENT_EMAIL,
  GIFT_CART_MODEL_PROPERTY_DGC_EMAIL,
  GIFT_CART_MODEL_PROPERTY_DGC_NAME,
  GIFT_CART_MODEL_PROPERTY_DGC_SEND_ON,
} from 'constants/digitalGiftCard';
import { MSRP_PRICE_TIER } from 'constants/pdp';
import Model from 'models';
import Product from 'models/Product';
import Money, { PRICE_FORMAT } from 'utils/Money';

const CartLineItem = class extends Model {
  get available() {
    return get(this.variant, 'available');
  }

  get id() {
    return get(this._data, 'id');
  }

  get key() {
    return get(this._data, 'key');
  }

  get lineItemType() {
    return get(this._data, 'lineItemType', 'VARIANT');
  }

  get product() {
    if (!this._product) {
      this._product = new Product(get(this._data, 'product'));
    }

    return this._product;
  }

  get variant() {
    return get(this._data, 'variant');
  }

  get sku() {
    return get(this.variant, 'sku');
  }

  get masterProductName() {
    return get(this.product, ['masterName']);
  }

  get masterProductId() {
    return get(this.product, ['masterId']);
  }

  get productTitle() {
    return get(this.product, ['fullName']);
  }

  get size() {
    return get(this.variant, ['value']);
  }

  get color() {
    return get(this.product, ['colorName']);
  }

  get featuredImage() {
    return get(this.product, ['featuredImage']);
  }

  get image() {
    return get(this._data, 'image');
  }

  get fullName() {
    return get(this._data, 'title');
  }

  get type() {
    return get(this.product, 'type');
  }

  get imageAltText() {
    return this.fullName;
  }

  get price() {
    return get(this._data, 'price');
  }

  get formattedPrice() {
    const price = get(this._data, 'price');
    if (!isFinite(price)) {
      return null;
    }

    const priceSubject = new Money(price);

    if (Number.isInteger(priceSubject.units)) {
      return priceSubject.format();
    }

    if (!get(this._data, 'formatted_price') && !Number.isInteger(priceSubject.units)) {
      return priceSubject.format(PRICE_FORMAT.unitsAndSubUnits);
    }

    return get(this._data, 'formatted_price');
  }

  get quantity() {
    return get(this._data, 'quantity', 0);
  }

  get originalPrice() {
    return get(this._data, 'original_price');
  }

  get formattedOriginalPrice() {
    const originalPriceSubject = new Money(this.originalPrice);

    if (Number.isInteger(originalPriceSubject.units)) {
      return new Money(this.originalPrice).format();
    }
    return get(this._data, 'formatted_original_price');
  }

  get compareAtPrice() {
    return get(this.variant, 'compareAtPrice');
  }

  get formattedCompareAtPrice() {
    const compareAtPrice = get(this.variant, 'compareAtPrice');
    if (!isFinite(compareAtPrice)) {
      return null;
    }
    return new Money(compareAtPrice).format();
  }

  get totalDiscount() {
    return get(this._data, 'total_discount');
  }

  get url() {
    return get(this._data, 'url');
  }

  get properties() {
    return get(this._data, 'properties');
  }

  get gender() {
    return this.product.formattedGender;
  }

  get isUnisize() {
    return this.product.isUnisize;
  }

  get backorderMessage() {
    return get(this.product, 'backorderMessage');
  }

  get isBackordered() {
    return get(this.variant, 'backordered');
  }

  get isFinalSale() {
    return get(this.variant, 'isFinalSale');
  }

  get isDigitalGiftCardWithRecipientEmail() {
    return get(this._data, GIFT_CART_MODEL_PROPERTY_IS_DGC_WITH_RECIPIENT_EMAIL);
  }

  get digitalGiftCardRecipientEmail() {
    return get(this._data, GIFT_CART_MODEL_PROPERTY_DGC_EMAIL);
  }

  get digitalGiftCardRecipientName() {
    return get(this._data, GIFT_CART_MODEL_PROPERTY_DGC_NAME);
  }

  get digitalGiftCardRecipientSendOn() {
    return get(this._data, GIFT_CART_MODEL_PROPERTY_DGC_SEND_ON);
  }

  get genericProductTitle() {
    return this.product.genericProductName;
  }

  get priceTier() {
    return get(this.product, ['priceTier']);
  }

  get isSaleProduct() {
    if (!this.priceTier) return null;
    return !(this.priceTier === MSRP_PRICE_TIER);
  }

  get isShoprunnerEligible() {
    const tags = get(this.product, ['tags']);
    return tags && tags.includes('shoprunner');
  }
};

export default CartLineItem;
